import { getClientTypeLabel, normalizeClientType, resolvePriceClientType } from './clientType';

export const normalizeAllowedClientTypesForPosScope = (values) => {
  if (!Array.isArray(values)) return [];
  const normalized = [];
  const seen = new Set();
  values.forEach((entry) => {
    const type = normalizeClientType(entry);
    if (!type || seen.has(type)) return;
    normalized.push(type);
    seen.add(type);
  });
  return normalized;
};

export const isPriceTypeAllowedForPosScope = (type, allowedClientTypes = []) => {
  const normalizedType = normalizeClientType(type);
  if (!normalizedType) return false;
  const normalizedAllowed = normalizeAllowedClientTypesForPosScope(allowedClientTypes);
  return !normalizedAllowed.length || normalizedAllowed.includes(normalizedType);
};

export const isPriceAllowedForPosScope = (price, allowedClientTypes = [], isSellablePrice) => {
  const sellable = typeof isSellablePrice === 'function'
    ? isSellablePrice(price)
    : Boolean(price);
  if (!sellable) return false;
  return isPriceTypeAllowedForPosScope(resolvePriceClientType(price), allowedClientTypes);
};

export const resolvePosLinePriceTypePayload = ({
  price,
  meta,
  allowedProductFormatCodes,
  allowedClientTypes = [],
  canSelectPriceType = false,
}) => {
  const selectedPriceType = normalizeClientType(resolvePriceClientType(price));
  const requestedPriceType = normalizeClientType(meta?.productFormatCode ?? meta?.priceType);
  const allowedCodes = allowedProductFormatCodes !== undefined ? allowedProductFormatCodes : allowedClientTypes;
  const canUseManualOverride = Boolean(canSelectPriceType)
    && Boolean(meta?.productFormatOverride ?? meta?.priceTypeOverride)
    && requestedPriceType
    && requestedPriceType === selectedPriceType
    && isPriceTypeAllowedForPosScope(requestedPriceType, allowedCodes);

  return {
    productFormatCode: selectedPriceType,
    productFormatOverride: canUseManualOverride,
    priceType: selectedPriceType,
    priceTypeOverride: canUseManualOverride,
    autoPriceSelection: Boolean(canSelectPriceType) && Boolean(meta?.autoPriceSelection),
  };
};

const normalizeQuantityValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildIssueName = (product, variety) => {
  const productName = String(product?.productName || '').trim();
  const varietyName = String(variety?.varietyName || '').trim();
  if (productName && varietyName && productName.toLowerCase() !== varietyName.toLowerCase()) {
    return `${productName} - ${varietyName}`;
  }
  return varietyName || productName || 'Producto';
};

export const collectDisallowedPosClientTypeIssues = ({
  products = [],
  quantities = {},
  scaleReadingLines = [],
  allowedProductFormatCodes,
  allowedClientTypes = [],
  isSellablePrice,
  resolveEffectivePrice,
} = {}) => {
  const normalizedAllowed = normalizeAllowedClientTypesForPosScope(
    allowedProductFormatCodes !== undefined ? allowedProductFormatCodes : allowedClientTypes
  );
  if (!normalizedAllowed.length) return [];

  const issues = [];
  const addIssue = ({ product, variety, price, priceIndex, quantity, line }) => {
    if (normalizeQuantityValue(quantity) <= 0) return;
    const effectivePrice = typeof resolveEffectivePrice === 'function'
      ? (resolveEffectivePrice({ product, variety, price, priceIndex, quantity, line }) || price)
      : price;
    if (isPriceAllowedForPosScope(effectivePrice, normalizedAllowed, isSellablePrice)) return;
    const priceType = normalizeClientType(resolvePriceClientType(effectivePrice));
    issues.push({
      productId: product?.id ?? null,
      varietyId: variety?.id ?? null,
      varietyPriceId: effectivePrice?.id ?? price?.id ?? null,
      priceIndex,
      effectivePriceIndex: effectivePrice === price
        ? priceIndex
        : (variety?.VarietyPrices || []).findIndex((entry) => Number(entry?.id) === Number(effectivePrice?.id)),
      priceType,
      productFormatCode: priceType,
      priceTypeLabel: getClientTypeLabel(priceType),
      productFormatLabel: getClientTypeLabel(priceType),
      allowedProductFormatCodes: normalizedAllowed,
      allowedClientTypes: normalizedAllowed,
      displayName: buildIssueName(product, variety),
    });
  };

  (products || []).forEach((product) => {
    (product?.Varieties || []).forEach((variety) => {
      (variety?.VarietyPrices || []).forEach((price, priceIndex) => {
        addIssue({
          product,
          variety,
          price,
          priceIndex,
          quantity: quantities?.[variety.id]?.[priceIndex],
          line: null,
        });
      });
    });
  });

  (scaleReadingLines || []).forEach((line) => {
    const product = (products || []).find((entry) =>
      (entry?.Varieties || []).some((variety) => Number(variety?.id) === Number(line?.varietyId))
    );
    const variety = (product?.Varieties || []).find(
      (entry) => Number(entry?.id) === Number(line?.varietyId)
    );
    if (!product || !variety) return;
    const priceIndex = Number.isInteger(line?.priceIndex)
      ? line.priceIndex
      : (variety.VarietyPrices || []).findIndex(
        (price) => Number(price?.id) === Number(line?.varietyPriceId)
      );
    const price = variety.VarietyPrices?.[priceIndex];
    addIssue({
      product,
      variety,
      price,
      priceIndex,
      quantity: line?.quantity,
      line,
    });
  });

  return issues;
};

export const buildDisallowedClientTypeMessage = (
  issues,
  scopeLabel = 'venta enviada a caja'
) => {
  const list = Array.isArray(issues) ? issues : [];
  if (!list.length) return '';
  const names = Array.from(new Set(list.map((issue) => issue.displayName).filter(Boolean)));
  const visibleNames = names.slice(0, 3).join(', ');
  const suffix = names.length > 3 ? ` y ${names.length - 3} más` : '';
  return `Hay productos con formato no permitido para esta ${scopeLabel}: ${visibleNames}${suffix}. Cambia el formato de venta o retira esos productos.`;
};
